/**
 * Die Figureninformation — §51.
 *
 * §51 nennt sechs Angaben und gibt ein Beispiel: Name, Bewegung, Schlag, Sonderfähigkeit. Dazu
 * Besitzer und Team, weil auf einem Brett mit sechs Farben nicht jede Figur aus sich heraus
 * erkennen lässt, wem sie gehört.
 *
 * **Die Zahlen kommen aus der RuleConfig der Partie** (`game/pieceInfo.ts`), nicht von hier und
 * nicht aus der globalen Konfiguration. Das ist der Projektzweck: wer im Balancing schraubt,
 * soll in **seiner** Partie sehen, was gilt (`CLAUDE.md` Regel 2 und 3).
 *
 * **„Kein normaler Schlag" ist eine Aussage, keine fehlende Zahl.** Fähnrich und Stoßer schlagen
 * nicht auf die übliche Weise (§14, §18); eine `0` dort wäre schlicht falsch, und eine leere
 * Zeile ließe offen, ob die Angabe fehlt oder die Fähigkeit.
 *
 * **Die Sonderfähigkeiten folgen den Werten, nicht der Figurenklasse.** Dass der Springer
 * springt, steht nicht hier — es steht in `movement.canJump`. Im Balancing kann auch ein Wächter
 * springen, und dann sagt diese Anzeige das.
 */

import { useTranslation } from 'react-i18next';

import { SECTOR_COLORS } from '../board/palette.js';
import type { PieceInfo, PieceRange } from './pieceInfo.js';

export interface PieceInfoPanelProps {
  readonly info: PieceInfo | undefined;
  /** Anzeigenamen aus der Lobby-Ansicht; fehlt einer, bleibt die Kennung stehen. */
  readonly names: ReadonlyMap<string, string>;
}

export function PieceInfoPanel({ info, names }: PieceInfoPanelProps) {
  const { t } = useTranslation(['play', 'pieces', 'sectors']);

  if (info === undefined) {
    return null;
  }

  /** Eine Reichweite in Worten — fest, von–bis, oder unbegrenzt. */
  const range = ({ min, max }: PieceRange): string => {
    if (max === null) return t('play:info.rangeUnlimited', { min });
    if (max === min) return t('play:info.rangeExact', { min });
    return t('play:info.rangeSpan', { min, max });
  };

  const abilities: readonly string[] = [
    ...(info.canJump ? [t('play:info.jump')] : []),
    ...(info.push === undefined
      ? []
      : [
          t('play:info.push', {
            range: range({ min: info.push.range.min, max: info.push.range.max }),
            distance: info.push.distance,
          }),
          info.push.pusherAdvance === 0
            ? t('play:info.pushStays')
            : t('play:info.pushAdvance', { advance: info.push.pusherAdvance }),
          ...(info.push.canPushOffBoard ? [t('play:info.pushOffBoard')] : []),
          ...(info.push.canPushFlagBearerFromGoal ? [t('play:info.pushFromGoal')] : []),
        ]),
    ...(info.flagBearer ? [t('play:info.flagBearer')] : []),
  ];

  return (
    <section data-piece-info={info.type}>
      <h3 style={{ color: SECTOR_COLORS[info.sector].stroke }}>{t(`pieces:${info.type}`)}</h3>
      <ul>
        <li>{t('play:info.owner', { name: names.get(info.owner) ?? info.owner })}</li>
        {info.team === undefined ? null : <li>{t('play:teamName', { team: info.team })}</li>}
        <li>{t('play:info.movement', { range: range(info.movement) })}</li>
        <li>
          {info.capture === undefined
            ? t('play:info.noCapture')
            : t('play:info.capture', { range: range(info.capture) })}
        </li>
        {abilities.map((ability) => (
          <li key={ability}>{ability}</li>
        ))}
      </ul>
    </section>
  );
}
